import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';
import {
  ArrowLeftIcon,
  CheckIcon,
  EyeIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  UserIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const AssessmentReview = ({
  isModal = false,
  assessmentId: propAssessmentId,
  jobPostingId: propJobPostingId,
  onBack,
  onContinue,
  registerPublish,
}) => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const assessmentId = propAssessmentId || routeId;
  const location = useLocation();

  // Check if coming from HireLoop
  const returnTo = location.state?.returnTo;
  const fromHireLoop = location.state?.fromHireLoop;
  const jobPostingId = propJobPostingId || location.state?.jobPostingId;
  const backgroundLocation = location.state?.backgroundLocation;
  const modal = location.state?.modal;
  const activeTab = location.state?.activeTab;

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [expandedTests, setExpandedTests] = useState(new Set());

  useEffect(() => {
    if (assessmentId) {
      fetchAssessment();
    }
  }, [assessmentId]);

  useEffect(() => {
    if (assessment && assessment.content && assessment.content.tests) {
      const allIds = new Set();
      assessment.content.tests.forEach((t) => {
        if (t.test && (t.test._id || t.test.id)) {
          allIds.add(t.test._id || t.test.id);
        }
      });
      setExpandedTests(allIds);
    }
  }, [assessment]);

  // Debug: Log assessment data when it changes
  useEffect(() => {
    if (assessment) {
      logger.log('Assessment data received:', assessment);
      if (assessment.content && assessment.content.questions) {
        logger.log('Questions data:', assessment.content.questions);
        assessment.content.questions.forEach((q, i) => {
          logger.log(`Question ${i + 1}:`, q.question);
        });
      }
    }
  }, [assessment]);

  const fetchAssessment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/assessments/${assessmentId}`);
      logger.log('API Response:', response.data);
      setAssessment(response.data.assessment);
    } catch (error) {
      console.error('Error fetching assessment:', error);
      toast.error('Failed to load assessment');
      navigate('/dashboard/assessments');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, navigate]);

  const handlePublish = useCallback(async () => {
    try {
      setPublishing(true);
      const response = await axios.post(`/api/assessments/${assessmentId}/publish`);

      toast.success(response.data.message || 'Assessment published successfully!');

      // Navigate based on context
      if (isModal) {
        if (typeof onContinue === 'function') onContinue();
        return true;
      }

      if (fromHireLoop) {
        // Navigate to invite page first, then return to HireLoop
        navigate(`/dashboard/assessments/${assessmentId}/invite`, {
          state: {
            returnTo: returnTo,
            fromHireLoop: true,
            jobPostingId: jobPostingId,
            screeningChoice: location.state?.screeningChoice,
            jobData: location.state?.jobData, // Pass jobData through
            backgroundLocation,
            modal,
            activeTab,
          },
        });
      } else {
        // Navigate to invite candidates page
        navigate(`/dashboard/assessments/${assessmentId}/invite`);
      }
    } catch (error) {
      console.error('Error publishing assessment:', error);

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to publish assessment');
      }
    } finally {
      setPublishing(false);
    }
    return false;
  }, [
    assessmentId,
    activeTab,
    fromHireLoop,
    isModal,
    jobPostingId,
    location.state,
    modal,
    navigate,
    onContinue,
    returnTo,
    backgroundLocation,
  ]);

  useEffect(() => {
    if (isModal && typeof registerPublish === 'function') {
      registerPublish(() => handlePublish);
    }
  }, [isModal, registerPublish, handlePublish]);

  const handleBackToEdit = () => {
    if (isModal) {
      if (typeof onBack === 'function') onBack();
      return;
    }

    if (fromHireLoop) {
      navigate(`/dashboard/assessments/${assessmentId}/build`, {
        state: {
          returnTo: returnTo,
          fromHireLoop: true,
          jobPostingId: jobPostingId,
          screeningChoice: location.state?.screeningChoice,
          jobData: location.state?.jobData,
          backgroundLocation: backgroundLocation,
          modal: modal,
          activeTab: activeTab,
        },
      });
    } else {
      navigate(`/dashboard/assessments/${assessmentId}/build`);
    }
  };

  const toggleTestExpansion = (testId) => {
    setExpandedTests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(testId)) {
        newSet.delete(testId);
      } else {
        newSet.add(testId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className={isModal ? 'max-w-none px-0' : 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'}>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className={isModal ? 'max-w-none px-0' : 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'}>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Assessment not found</h1>
            <button
              onClick={() => navigate('/dashboard/assessments')}
              className="text-indigo-600 hover:text-indigo-500"
            >
              Back to Assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalContent =
    assessment.content.tests.length +
    assessment.content.questions.length +
    assessment.content.customQuestions.length;

  return (
    <div className={isModal ? '' : 'min-h-screen bg-gray-50 py-8'}>
      <div className={isModal ? 'max-w-none px-0' : 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'}>
        {!isModal && (
          <>
            {/* Header */}
            <div className="mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{assessment.title}</h1>
                <p className="mt-2 text-gray-600">
                  Step 3: Review your assessment before publishing
                </p>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <div className="bg-green-600 text-white rounded-full h-8 w-8 flex items-center justify-center text-sm font-medium">
                    <CheckIcon className="h-4 w-4" />
                  </div>
                  <div className="ml-2 text-sm font-medium text-green-600">Metadata</div>
                </div>
                <div className="mx-4 h-px w-16 bg-green-600"></div>
                <div className="flex items-center">
                  <div className="bg-green-600 text-white rounded-full h-8 w-8 flex items-center justify-center text-sm font-medium">
                    <CheckIcon className="h-4 w-4" />
                  </div>
                  <div className="ml-2 text-sm font-medium text-green-600">Build Content</div>
                </div>
                <div className="mx-4 h-px w-16 bg-indigo-600"></div>
                <div className="flex items-center">
                  <div className="bg-indigo-600 text-white rounded-full h-8 w-8 flex items-center justify-center text-sm font-medium">
                    3
                  </div>
                  <div className="ml-2 text-sm font-medium text-indigo-600">Review & Publish</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Assessment Overview */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Assessment Overview</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Title:</span>
                  <span className="ml-2">{assessment.title}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <UserIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Role:</span>
                  <span className="ml-2">{assessment.role}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Duration:</span>
                  <span className="ml-2">{assessment.duration} minutes</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Passing Score:</span>
                  <span className="ml-2">{assessment.passingScore}%</span>
                </div>
              </div>
            </div>

            {/* Content Summary */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Content Summary</h3>
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Tests:</span>
                  <span className="ml-2">{assessment.content.tests.length}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <QuestionMarkCircleIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Total Questions:</span>
                  <span className="ml-2">{assessment.totalQuestions || 0}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Custom Questions:</span>
                  <span className="ml-2">{assessment.content.customQuestions.length}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  <span className="font-medium">Total Points:</span>
                  <span className="ml-2">{assessment.totalPoints || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {assessment.description && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Description</h3>
              <p className="text-gray-600">{assessment.description}</p>
            </div>
          )}

          {/* Instructions */}
          {assessment.instructions && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Instructions for Candidates
              </h3>
              <p className="text-gray-600">{assessment.instructions}</p>
            </div>
          )}
        </div>

        {/* Content Preview */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Content Preview</h2>
          </div>

          {/* Tests */}
          {assessment.content.tests.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Tests from Library</h3>
              <div className="space-y-3">
                {assessment.content.tests.map((testItem, index) => (
                  <div
                    key={testItem.test}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Test Header */}
                    <div
                      className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => toggleTestExpansion(testItem.test._id)}
                    >
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-500 mr-3">#{index + 1}</span>
                        <div>
                          <div className="font-medium text-gray-900">{testItem.test.title}</div>
                          <div className="text-sm text-gray-600">{testItem.test.description}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {testItem.test.numberOfQuestions} questions •{' '}
                            {testItem.test.estimatedTime} min
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            testItem.test.difficulty === 'Easy'
                              ? 'bg-green-100 text-green-800'
                              : testItem.test.difficulty === 'Medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {testItem.test.difficulty}
                        </span>
                        {expandedTests.has(testItem.test._id) ? (
                          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                        )}
                      </div>
                    </div>

                    {/* Test Questions (Expandable) */}
                    {expandedTests.has(testItem.test._id) && testItem.test.questions && (
                      <div className="border-t border-gray-200 bg-white p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Questions in this test:
                        </h4>
                        <div className="space-y-3">
                          {testItem.test.questions.map((questionItem, qIndex) => {
                            const question = questionItem.question; // Access the populated question
                            if (!question) return null;

                            return (
                              <div
                                key={question._id || qIndex}
                                className="pl-4 border-l-2 border-gray-200"
                              >
                                <div className="text-sm font-medium text-gray-900 mb-2">
                                  Q{qIndex + 1}: {question.title}
                                </div>
                                <div className="text-xs text-gray-600 mb-2">{question.content}</div>

                                {/* Show options whenever options exist */}
                                {question.options && question.options.length > 0 && (
                                  <div className="mb-2">
                                    <div className="text-xs font-medium text-gray-700 mb-1">
                                      Options:
                                    </div>
                                    <div className="space-y-1">
                                      {question.options.map((option, optIndex) => (
                                        <div key={optIndex} className="flex items-center space-x-2">
                                          <span
                                            className={`text-xs px-2 py-1 rounded ${
                                              option.isCorrect
                                                ? 'bg-green-100 text-green-800 font-medium'
                                                : 'bg-gray-100 text-gray-600'
                                            }`}
                                          >
                                            {String.fromCharCode(65 + optIndex)}. {option.text}
                                          </span>
                                          {option.isCorrect && (
                                            <span className="text-xs text-green-600 font-medium">
                                              ✓ Correct
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Show correct answer for other question types */}
                                {(!question.options || question.options.length === 0) &&
                                  question.correctAnswer && (
                                    <div className="mb-2">
                                      <div className="text-xs font-medium text-gray-700">
                                        Correct Answer:
                                      </div>
                                      <div className="text-xs text-gray-600 bg-green-50 px-2 py-1 rounded">
                                        {question.correctAnswer}
                                      </div>
                                    </div>
                                  )}

                                <div className="text-xs text-gray-500">
                                  {question.type} • {questionItem.points || 1} point
                                  {(questionItem.points || 1) !== 1 ? 's' : ''}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Questions */}
          {assessment.content.questions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Questions from Library</h3>
              <div className="space-y-3">
                {assessment.content.questions.map((questionItem, index) => {
                  const question = questionItem.question;
                  if (!question) return null;

                  return (
                    <div
                      key={question._id || index}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Question Header */}
                      <div className="p-4 bg-gray-50">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-500 mr-3">
                            #{index + 1}
                          </span>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{question.title}</div>
                            <div className="text-sm text-gray-600">{question.content}</div>
                          </div>
                        </div>
                      </div>

                      {/* Question Details */}
                      <div className="p-4 bg-white border-t border-gray-200">
                        {/* Show options whenever options exist */}
                        {question.options && question.options.length > 0 && (
                          <div className="mb-3">
                            <div className="text-sm font-medium text-gray-700 mb-2">Options:</div>
                            <div className="space-y-2">
                              {question.options.map((option, optIndex) => (
                                <div key={optIndex} className="flex items-center space-x-2">
                                  <span
                                    className={`text-sm px-3 py-2 rounded border ${
                                      option.isCorrect
                                        ? 'bg-green-100 text-green-800 border-green-300 font-medium'
                                        : 'bg-gray-50 text-gray-700 border-gray-200'
                                    }`}
                                  >
                                    {String.fromCharCode(65 + optIndex)}. {option.text}
                                  </span>
                                  {option.isCorrect && (
                                    <span className="text-sm text-green-600 font-medium">
                                      ✓ Correct
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Show correct answer for other question types */}
                        {(!question.options || question.options.length === 0) &&
                          question.correctAnswer && (
                            <div className="mb-3">
                              <div className="text-sm font-medium text-gray-700">
                                Correct Answer:
                              </div>
                              <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200">
                                {question.correctAnswer}
                              </div>
                            </div>
                          )}

                        <div className="text-xs text-gray-500">
                          {question.type} • {questionItem.points || 1} point
                          {(questionItem.points || 1) !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Questions */}
          {assessment.content.customQuestions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Custom Questions</h3>
              <div className="space-y-3">
                {assessment.content.customQuestions.map((question, index) => (
                  <div
                    key={question._id}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Question Header */}
                    <div className="p-4 bg-gray-50">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-500 mr-3">#{index + 1}</span>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{question.title}</div>
                          <div className="text-sm text-gray-600">{question.content}</div>
                        </div>
                      </div>
                    </div>

                    {/* Question Details */}
                    <div className="p-4 bg-white border-t border-gray-200">
                      {/* Show options for multiple choice questions */}
                      {(question.type === 'multiple-choice' ||
                        question.type === 'Multiple Choice (Single Answer)' ||
                        question.type === 'Multiple Choice (Multiple Answers)') &&
                        question.options &&
                        question.options.length > 0 && (
                          <div className="mb-3">
                            <div className="text-sm font-medium text-gray-700 mb-2">Options:</div>
                            <div className="space-y-2">
                              {question.options.map((option, optIndex) => (
                                <div key={optIndex} className="flex items-center space-x-2">
                                  <span
                                    className={`text-sm px-3 py-2 rounded border ${
                                      option.isCorrect
                                        ? 'bg-green-100 text-green-800 border-green-300 font-medium'
                                        : 'bg-gray-50 text-gray-700 border-gray-200'
                                    }`}
                                  >
                                    {String.fromCharCode(65 + optIndex)}. {option.text}
                                  </span>
                                  {option.isCorrect && (
                                    <span className="text-xs text-green-600 font-medium">
                                      ✓ Correct
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Show correct answer for other question types */}
                      {question.type !== 'multiple-choice' && question.correctAnswer && (
                        <div className="mb-3">
                          <div className="text-sm font-medium text-gray-700">Correct Answer:</div>
                          <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded border border-green-200">
                            {question.correctAnswer}
                          </div>
                        </div>
                      )}

                      <div className="text-xs text-gray-500">
                        {question.type} • {question.points} point{question.points !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Content Warning */}
          {totalContent === 0 && (
            <div className="text-center py-8 text-gray-500">
              <QuestionMarkCircleIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No content added</h3>
              <p className="text-gray-600">
                You need to add at least one test, question, or custom question before publishing.
              </p>
            </div>
          )}
        </div>

        {!isModal && (
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleBackToEdit}
              className="px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Back to Edit
            </button>

            <button
              onClick={handlePublish}
              disabled={publishing || totalContent === 0}
              className="px-6 py-3 border border-transparent rounded-md text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {publishing ? 'Publishing...' : 'Publish Assessment'}
            </button>
          </div>
        )}

        {/* Publishing Requirements */}
        {totalContent === 0 && (
          <div className="mt-6 text-center">
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="text-yellow-800">
                <p className="text-sm">
                  <strong>Note:</strong> You must add content to your assessment before publishing.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentReview;
